import { useState } from "react";
import Image from "next/image";
import { useAuthStore } from "@/stores/auth.store";
import { useToast } from "@/stores/ui.store";
import { Button } from "@/components/ui/button";
import { asset } from "@/lib/asset";
import {
  purchaseBundle,
  PurchaseCancelledError,
} from "@/services/payments/jest-payments";

const COIN_BUNDLES = [
  { id: "coins_1000", coins: 1000, price: "$9.99", label: "1,000 Coins" },
  {
    id: "coins_2500",
    coins: 2500,
    price: "$19.99",
    label: "2,500 Coins",
    bonus: 10,
  },
  {
    id: "coins_5500",
    coins: 5500,
    price: "$39.99",
    label: "5,500 Coins",
    bonus: 15,
  },
];

export default function StorePage() {
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { isAuthenticated, accessToken, user, updateBalance } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();

  const handleBuy = async (bundleId: string) => {
    if (!isAuthenticated || !accessToken) {
      showError("Sign In Required", "Please sign in to purchase coins");
      return;
    }
    if (typeof window.JestSDK === "undefined") {
      showError(
        "Payments Unavailable",
        "Jest payments are not loaded in this environment",
      );
      return;
    }

    try {
      setLoadingId(bundleId);
      const { coinsAdded } = await purchaseBundle(
        window.JestSDK,
        bundleId,
        accessToken,
      );

      if (user && coinsAdded > 0) {
        updateBalance({ coins: user.coins + coinsAdded });
      }
      showSuccess(
        "Purchase Complete",
        coinsAdded > 0
          ? `${coinsAdded.toLocaleString()} coins added to your balance.`
          : "Coins have been added to your balance.",
      );
    } catch (err) {
      if (err instanceof PurchaseCancelledError) {
        // Player dismissed Jest's checkout modal — silent no-op.
        return;
      }
      showError(
        "Purchase Failed",
        err instanceof Error ? err.message : "Something went wrong",
      );
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 pt-6 sm:pt-8" />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <span className="text-gold text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase mb-3 sm:mb-4 block">
            The Mint
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-cream mb-2">
            Buy Coins
          </h1>
          <p className="text-sm sm:text-base text-warm-gray">
            Purchase coins to enter events, buy props, and boost your picks.
          </p>
        </div>

        {/* Coin Bundles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {COIN_BUNDLES.map((bundle, i) => (
            <div
              key={bundle.id}
              className="relative p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/30 hover:border-gold/50 transition-colors"
            >
              {bundle.bonus && (
                <span className="absolute -top-2 -right-2 px-2.5 py-1 rounded-full bg-verdigris text-cream text-xs font-bold">
                  +{bundle.bonus}% BONUS
                </span>
              )}
              <div className="flex items-center gap-3 mb-5 sm:mb-6">
                <Image
                  src={asset("assets/icons/coins.webp")}
                  alt="Coins"
                  width={48}
                  height={48}
                  unoptimized
                  className="w-11 h-11 sm:w-12 sm:h-12 object-contain"
                />
                <div>
                  <p className="text-2xl sm:text-3xl font-bold text-cream">
                    {bundle.coins.toLocaleString()}
                  </p>
                  <p className="text-sm text-warm-gray">Coins</p>
                </div>
              </div>
              <Button
                className="w-full"
                variant="coins"
                size="lg"
                disabled={loadingId !== null}
                onClick={() => handleBuy(bundle.id)}
              >
                {loadingId === bundle.id ? "Processing..." : bundle.price}
              </Button>
            </div>
          ))}
        </div>

        {/* Info note */}
        <div className="mt-6 sm:mt-8 p-4 rounded-xl bg-surface-2 border border-surface-3 text-xs sm:text-sm text-warm-gray">
          <p>
            Payments are processed securely via Jest. Coins are credited
            instantly after successful payment.
          </p>
        </div>
      </div>
    </div>
  );
}
